import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PopupDialog, ActiveStatusSwitch } from '../../../../components/common';
import { TextFieldComponent } from '../../../../components/common/TextFieldComponent';
import { AsociacionSelect } from '../../asociacion/components/AsociacionSelect';
import { TipoIdentificacionSelect, GeneroSelect, GrupoSelect } from '../../../../components/parametros';
import { type ProveedorEditDialogProps, type ProveedorResponse, type ProveedorRequest } from '../types/proveedor';
import { useProveedorForm } from '../hooks';
import { PROVEEDOR_CONSTANTS } from '../../../../constants/proveedor';
import { ProveedorService } from '../services';


// Helper function to convert ProveedorResponse to ProveedorRequest
const convertToProveedorRequest = (response: ProveedorResponse): ProveedorRequest => ({
  idPersona: response.idPersona,
  razonSocial: response.razonSocial,
  nombreFantasia: response.nombreFantasia || undefined,
  personaContacto: response.personaContacto || undefined,
  idTipoIdentificacion: response.idTipoIdentificacion,
  identificacion: response.identificacion,
  idGenero: response.idGenero,
  idEstado: 1, // Default active state
  idGrupo: response.idGrupo,
  idAsociacion: response.idAsociacion,
  direccion: response.direccion || undefined,
  correoContacto: response.correoElectronico || undefined,
  telefono: response.telefono || undefined,
});

/**
 * EditProveedor Component
 *
 * A comprehensive proveedor editing dialog that combines form fields and dialog functionality.
 * Features:
 * - Complete proveedor information form
 * - Validation and error handling
 * - Loading states and submission
 * - Responsive layout design
 * - Clean architecture with separated concerns
 */
const EditProveedor: React.FC<ProveedorEditDialogProps> = ({
  open,
  proveedorIdPersona,
  onClose,
  onSave,
  title = PROVEEDOR_CONSTANTS.DIALOG_CONFIG.DEFAULT_TITLE,
}) => {

  // Local state for proveedor data
  const [, setProveedorData] = useState<ProveedorResponse | null>(null);
  const [loadingProveedor, setLoadingProveedor] = useState(false);
  const [initialProveedorRequest, setInitialProveedorRequest] = useState<ProveedorRequest | null>(null);

  const {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
  } = useProveedorForm({
    initialProveedorRequest: initialProveedorRequest,
    isOpen: open,
    onSubmit: onSave,
  });

  // Fetch proveedor data when dialog opens
  useEffect(() => {
    const fetchProveedor = async () => {
      if (!open || !proveedorIdPersona) {
        setInitialProveedorRequest(null);
        setProveedorData(null);
        return;
      }

      try {
        setLoadingProveedor(true);
        const data = await ProveedorService.getProveedorById(proveedorIdPersona);
        setProveedorData(data);
        setInitialProveedorRequest(convertToProveedorRequest(data));
      } catch (error) {
        console.error('Error fetching proveedor:', error);
        setProveedorData(null);
        setInitialProveedorRequest(null);
      } finally {
        setLoadingProveedor(false);
      }
    };

    fetchProveedor();
  }, [open, proveedorIdPersona]);

  // Handle new proveedor case (no proveedorIdPersona)
  useEffect(() => {
    if (open && !proveedorIdPersona) {
      setInitialProveedorRequest(null);
      setProveedorData(null);
    }
  }, [open, proveedorIdPersona]);


  const handleCancelClick = (): void => {
    if (isDirty) {
      // In a real application, you might want to show a confirmation dialog
      // before discarding changes
    }
    onClose();
  };

  // Early return if no form data or still loading
  if (!formData || (proveedorIdPersona && loadingProveedor)) {
    return null;
  }

  const dialogTitle = `${title}${isDirty ? ' *' : ''}`;
  const { FIELD_LABELS } = PROVEEDOR_CONSTANTS;



  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PopupDialog
        open={open}
        onClose={handleCancelClick}
        title={dialogTitle}
        maxWidth={PROVEEDOR_CONSTANTS.DIALOG_CONFIG.MAX_WIDTH}
        disableEscapeKeyDown={isDirty}
        primaryAction={{
          label: 'Guardar',
          onClick: handleSubmit,
          disabled: !isValid,
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleCancelClick,
        }}
      >
        <Box sx={{ mt: 2 }}>
          {/* Row 1: Razón Social */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextFieldComponent
                label={FIELD_LABELS.razonSocial}
                value={formData.razonSocial}
                onChange={(value: string) => handleFieldChange('razonSocial', value)}
                error={errors.razonSocial}
              />
            </Box>
          </Box>

          {/* Row 2: Nombre Fantasía, Persona de Contacto */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 250px' }}>
              <TextFieldComponent
                label={FIELD_LABELS.nombreFantasia}
                value={formData.nombreFantasia || ''}
                onChange={(value: string) => handleFieldChange('nombreFantasia', value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <TextFieldComponent
                label={FIELD_LABELS.personaContacto}
                value={formData.personaContacto || ''}
                onChange={(value: string) => handleFieldChange('personaContacto', value)}
              />
            </Box>
          </Box>

          {/* Row 3: Tipo Identificación, Identificación, Género, Estado */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 150px' }}>
              <TipoIdentificacionSelect
                label={FIELD_LABELS.idTipoIdentificacion}
                value={formData.idTipoIdentificacion}
                onChange={(value: string | number) => handleFieldChange('idTipoIdentificacion', value)}
                error={errors.idTipoIdentificacion}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <TextFieldComponent
                label={FIELD_LABELS.identificacion}
                value={formData.identificacion}
                onChange={(value: string) => handleFieldChange('identificacion', value)}
                error={errors.identificacion}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <GeneroSelect
                label={FIELD_LABELS.idGenero}
                value={formData.idGenero}
                onChange={(value: string | number) => handleFieldChange('idGenero', value)}
                error={errors.idGenero}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px', display: 'flex', flexDirection: 'column' }}>
              <ActiveStatusSwitch
                label={FIELD_LABELS.idEstado}
                value={formData.idEstado === 1}
                onChange={(checked: boolean) => handleFieldChange('idEstado', checked ? 1 : 0)}
              />
            </Box>
          </Box>

          {/* Row 4: Grupo, Asociación */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 250px' }}>
              <GrupoSelect
                label={FIELD_LABELS.idGrupo}
                value={formData.idGrupo}
                onChange={(value: string | number) => handleFieldChange('idGrupo', value)}
                error={errors.idGrupo}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <AsociacionSelect
                label={FIELD_LABELS.idAsociacion}
                value={formData.idAsociacion}
                onChange={(value: string | number) => handleFieldChange('idAsociacion', value)}
                error={errors.idAsociacion}
              />
            </Box>
          </Box>

          {/* Row 5: Dirección */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextFieldComponent
                label={FIELD_LABELS.direccion}
                value={formData.direccion || ''}
                onChange={(value: string) => handleFieldChange('direccion', value)}
              />
            </Box>
          </Box>

          {/* Row 6: Email, Teléfono */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 250px' }}>
              <TextFieldComponent
                label={FIELD_LABELS.correoContacto}
                type="email"
                value={formData.correoContacto || ''}
                onChange={(value: string) => handleFieldChange('correoContacto', value)}
                error={errors.correoContacto}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <TextFieldComponent
                label={FIELD_LABELS.telefono}
                value={formData.telefono || ''}
                onChange={(value: string) => handleFieldChange('telefono', value)}
                error={errors.telefono}
              />
            </Box>
          </Box>
        </Box>
      </PopupDialog>
    </LocalizationProvider>
  );
};

export default EditProveedor;